// Thin fetch wrapper. All requests hit /api/* — routed by Vite to localhost:8000
// in dev, and same-origin in prod (assuming the FastAPI backend is served
// behind the same reverse-proxy as the static build).

import type { FilterEntry } from "../state/filterStore";

const API_PREFIX = "/api";

// AGENT-STATIC: in the Cloudflare static-bake build there is no live backend.
// Short-circuit the handful of generic GETs this client makes (sidebar filter
// lists, the health pill, the legacy page index) to the baked artifacts or a
// graceful stub, so no /api/* request is ever issued.
const USE_STATIC =
  typeof import.meta !== "undefined" &&
  (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_DASH_STATIC === "true";

async function staticGet(path: string): Promise<unknown> {
  const p = path.startsWith("/") ? path : `/${path}`;
  if (p === "/dashboard/filters") {
    const r = await fetch("/hsq-data/filters.json", { headers: { Accept: "application/json" } });
    return r.ok ? r.json() : {};
  }
  if (p === "/health") return { ok: true, status: "ok", mode: "static" };
  if (p === "/pages") return [];
  // Other legacy endpoints (/pages/*, /measures, /visual/*, /bookmarks) aren't
  // baked — the dashboard view doesn't use them; degrade quietly.
  return null;
}

// P0: Serialize the Zustand filter map into the ?filters=<urlencoded JSON> shape
// that the backend's parse_filters() helper (webapp/backend/app/routers/_common.py:16)
// accepts. Each FilterEntry's store key is "Table.Column"; the backend expects
// records of {table, column, op, value} — see apply_filters() in
// webapp/backend/app/data_layer.py:323. Returns "" for empty/undefined filters
// so it can be concatenated directly onto a URL.
export function buildFilterQuery(filters?: Record<string, FilterEntry>): string {
  if (!filters) return "";
  const rows: Array<{ table: string; column: string; op: string; value: unknown }> = [];
  for (const [key, entry] of Object.entries(filters)) {
    // Key shape is "Table.Column"; split on the FIRST dot so column names with
    // dots survive (unusual, but defensive).
    const dot = key.indexOf(".");
    if (dot <= 0) continue;
    const table = key.slice(0, dot);
    const column = key.slice(dot + 1);
    rows.push({ table, column, op: entry.op, value: entry.value as unknown });
  }
  if (rows.length === 0) return "";
  return `?filters=${encodeURIComponent(JSON.stringify(rows))}`;
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `API error ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

interface RequestOpts {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /** Extra headers (Content-Type is auto-set for JSON bodies). */
  headers?: Record<string, string>;
}

export async function apiRequest<T>(
  path: string,
  opts: RequestOpts = {},
): Promise<T> {
  if (USE_STATIC) {
    return ((opts.method ?? "GET") === "GET" ? await staticGet(path) : null) as T;
  }
  const url = `${API_PREFIX}${path.startsWith("/") ? path : `/${path}`}`;
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.headers ?? {}),
  };
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }

  const res = await fetch(url, {
    method: opts.method ?? "GET",
    headers,
    body,
    signal: opts.signal,
    credentials: "same-origin",
  });

  const text = await res.text();
  const parsed = text ? safeParse(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, parsed ?? text, `${res.status} on ${url}`);
  }
  return parsed as T;
}

function safeParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    apiRequest<T>(path, { method: "GET", signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    apiRequest<T>(path, { method: "POST", body, signal }),
};
